// TanStack Query хуки на каждый endpoint. Кеш-ключи иерархические,
// invalidate сделан явным чтобы каждая мутация знала, что
// инвалидирует.

import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { api } from './api.ts';
import type {
  CreatedProduct,
  CreatedUser,
  DailyFunnelResponse,
  FunnelResponse,
  ImpressionRecord,
  OverviewResponse,
  Product,
  Theory,
  TrackingLink,
  User,
  UserSummary,
} from './types.ts';

// ── auth ─────────────────────────────────────────────────────────────────────

export function useMe(): UseQueryResult<{ user: User }> {
  return useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.get<{ user: User }>('/auth/me'),
    retry: false,
    staleTime: 30_000,
  });
}

// ── products ─────────────────────────────────────────────────────────────────

export function useProducts(includeArchived = false): UseQueryResult<{ items: Product[] }> {
  return useQuery({
    queryKey: ['products', { includeArchived }],
    queryFn: () =>
      api.get<{ items: Product[] }>('/products', { include_archived: includeArchived }),
  });
}

export function useProduct(id: string | undefined): UseQueryResult<{ product: Product }> {
  return useQuery({
    queryKey: ['product', id],
    queryFn: () => api.get<{ product: Product }>(`/products/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { slug: string; name: string }) =>
      api.post<CreatedProduct>('/products', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products'] }),
  });
}

export function useArchiveProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<{ product: Product }>(`/products/${id}/archive`),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: ['products'] });
      void qc.invalidateQueries({ queryKey: ['product', id] });
    },
  });
}

export function useRotateProductToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<CreatedProduct>(`/products/${id}/rotate-token`),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: ['product', id] });
    },
  });
}

// ── theories ─────────────────────────────────────────────────────────────────

export function useTheories(productId: string | undefined): UseQueryResult<{ items: Theory[] }> {
  return useQuery({
    queryKey: ['theories', productId],
    queryFn: () => api.get<{ items: Theory[] }>('/theories', { product_id: productId }),
    enabled: Boolean(productId),
  });
}

export function useTheory(id: string | undefined): UseQueryResult<{ theory: Theory }> {
  return useQuery({
    queryKey: ['theory', id],
    queryFn: () => api.get<{ theory: Theory }>(`/theories/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateTheory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { product_id: string; slug: string; name: string }) =>
      api.post<{ theory: Theory }>('/theories', body),
    onSuccess: (_data, vars) => qc.invalidateQueries({ queryKey: ['theories', vars.product_id] }),
  });
}

export function useUpdateTheory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; patch: { name?: string; status?: 'active' | 'archived' } }) =>
      api.patch<{ theory: Theory }>(`/theories/${vars.id}`, vars.patch),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['theories', data.theory.productId] });
      void qc.invalidateQueries({ queryKey: ['theory', data.theory.id] });
    },
  });
}

// ── links ────────────────────────────────────────────────────────────────────

export function useLinks(theoryId: string | undefined): UseQueryResult<{ items: TrackingLink[] }> {
  return useQuery({
    queryKey: ['links', theoryId],
    queryFn: () => api.get<{ items: TrackingLink[] }>('/links', { theory_id: theoryId }),
    enabled: Boolean(theoryId),
  });
}

export interface CreateLinkBody {
  theory_id: string;
  slug: string;
  source: string;
  destination_url: string;
  channel_title?: string;
  channel_avatar_url?: string;
  channel_description?: string;
  cta_label?: string;
}

export function useCreateLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateLinkBody) => api.post<{ link: TrackingLink }>('/links', body),
    onSuccess: (_data, vars) => qc.invalidateQueries({ queryKey: ['links', vars.theory_id] }),
  });
}

export interface UpdateLinkBody {
  source?: string;
  destination_url?: string;
  channel_title?: string | null;
  channel_avatar_url?: string | null;
  channel_description?: string | null;
  cta_label?: string | null;
}

// ── impressions ──────────────────────────────────────────────────────────────

export function useImpressions(
  linkId: string | undefined,
): UseQueryResult<{ items: ImpressionRecord[] }> {
  return useQuery({
    queryKey: ['impressions', linkId],
    queryFn: () => api.get<{ items: ImpressionRecord[] }>(`/links/${linkId}/impressions`),
    enabled: Boolean(linkId),
  });
}

export function useRecordImpression(linkId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { total_impressions: number; note?: string | null }) =>
      api.post<ImpressionRecord>(`/links/${linkId}/impressions`, body),
    onSuccess: () => {
      // Свежее значение влияет на: историю ссылки, funnel.impressions_total,
      // daily-impressions на графике. Инвалидируем все три.
      void qc.invalidateQueries({ queryKey: ['impressions', linkId] });
      void qc.invalidateQueries({ queryKey: ['analytics', 'funnel'] });
      void qc.invalidateQueries({ queryKey: ['analytics', 'daily'] });
    },
  });
}

// ── uploads ──────────────────────────────────────────────────────────────────

export function useUploadAvatar() {
  return useMutation({
    mutationFn: async (file: File): Promise<{ url: string }> => {
      const fd = new FormData();
      fd.append('file', file);
      return api.upload<{ url: string }>('/dashboard/uploads/avatar', fd);
    },
  });
}

export function useUpdateLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; patch: UpdateLinkBody }) =>
      api.patch<{ link: TrackingLink }>(`/links/${vars.id}`, vars.patch),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['links', data.link.theoryId] });
    },
  });
}

// ── analytics ────────────────────────────────────────────────────────────────

export function useOverview(window?: {
  from?: string;
  to?: string;
}): UseQueryResult<OverviewResponse> {
  return useQuery({
    queryKey: ['analytics', 'overview', window],
    queryFn: () => api.get<OverviewResponse>('/analytics/overview', window),
    staleTime: 15_000,
  });
}

export function useFunnel(
  theoryId: string | undefined,
  window?: { from?: string; to?: string },
): UseQueryResult<FunnelResponse> {
  return useQuery({
    queryKey: ['analytics', 'funnel', theoryId, window],
    queryFn: () => api.get<FunnelResponse>('/analytics/funnel', { theory_id: theoryId, ...window }),
    enabled: Boolean(theoryId),
    staleTime: 15_000,
  });
}

export interface DailyFunnelParams {
  /** YYYY-MM-DD, включительно. */
  from: string;
  /** YYYY-MM-DD, включительно. */
  to: string;
  /** IANA timezone (например, 'Europe/Moscow'). */
  tz: string;
}

export function useDailyFunnel(
  theoryId: string | undefined,
  params: DailyFunnelParams,
): UseQueryResult<DailyFunnelResponse> {
  return useQuery({
    queryKey: ['analytics', 'daily', theoryId, params],
    queryFn: () =>
      api.get<DailyFunnelResponse>('/analytics/daily', {
        theory_id: theoryId,
        from: params.from,
        to: params.to,
        tz: params.tz,
      }),
    enabled: Boolean(theoryId),
    staleTime: 15_000,
  });
}

// ── users ────────────────────────────────────────────────────────────────────

export function useUsers(): UseQueryResult<{ items: UserSummary[] }> {
  return useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<{ items: UserSummary[] }>('/users'),
  });
}

export interface CreateUserBody {
  email: string;
  password: string;
  role: 'owner' | 'admin';
  requester_password: string;
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateUserBody) => api.post<CreatedUser>('/users', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}
